"use client"

import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import { AvatarImage, Link, type LinkProps } from "@opensea/next-ui-kit"
import {
  type AddressDisplay,
  Avatar,
  type AvatarImageProps,
  classNames,
  FlexCenter,
  type FlexCenterProps,
  formatAddress,
  linkVariants,
  TextOverflowTooltip,
} from "@opensea/ui-kit"
import { type IconProps, Verified } from "@opensea/ui-kit/icons"
import type { Address } from "@opensea/utils"
import { isAddressEqual, isNullAddress } from "@opensea/utils"
import { useTranslations } from "next-intl"
import type React from "react"
import { createContext, useContext, useMemo } from "react"
import { AccountLockupFragment } from "./AccountLockup.fragment"

type AccountLockupProps = {
  account: FragmentOf<typeof AccountLockupFragment>
  badge?: React.ReactNode
  addressDisplay?: AddressDisplay
  isVerified?: boolean | null
  children?: React.ReactNode
  avatar?: React.ReactNode
  withAvatar?: boolean
  isLink?: boolean
  displayYou?: boolean
  showDisplayName?: boolean
  disableTextOverflowTooltip?: boolean
  collectionSlug?: string
  href?: LinkProps["href"]
  onNavigationCompleted?: LinkProps["onNavigationCompleted"]
  getProfileUrl?: (
    account: FragmentOf<typeof AccountLockupFragment>,
  ) => LinkProps["href"]
  connectedAddress: Address | null
  overrides?: {
    Link?: Partial<LinkProps>
  }
} & FlexCenterProps

const AccountLockupContext = createContext<{
  address: string
  addressDisplay: AddressDisplay
  displayName: string | null | undefined
  imageUrl: string | null | undefined
  isVerified: boolean | null | undefined
}>({
  address: "",
  addressDisplay: "standard",
  displayName: undefined,
  imageUrl: undefined,
  isVerified: false,
})

function AccountLockup({
  account: accountKey,
  children,
  className,
  addressDisplay = "standard",
  isVerified,
  badge,
  avatar,
  withAvatar,
  isLink = true,
  displayYou = true,
  showDisplayName = true,
  disableTextOverflowTooltip = false,
  collectionSlug,
  href,
  onNavigationCompleted,
  getProfileUrl,
  connectedAddress,
  overrides,
  ...rest
}: AccountLockupProps) {
  const account = readFragment(AccountLockupFragment, accountKey)
  const isCurrentUser =
    connectedAddress !== null &&
    isAddressEqual(connectedAddress, account.address)
  const disabled = isNullAddress(account.address)
  const t = useTranslations("AccountLockup")

  const resolvedDisplayName = useMemo(() => {
    if (isCurrentUser && displayYou) {
      return t("you")
    }
    return showDisplayName ? account.displayName : undefined
  }, [isCurrentUser, displayYou, showDisplayName, account.displayName, t])

  const value = useMemo(
    () => ({
      address: account.address,
      addressDisplay,
      displayName: resolvedDisplayName,
      imageUrl: account.imageUrl,
      isVerified,
    }),
    [
      account.address,
      addressDisplay,
      resolvedDisplayName,
      account.imageUrl,
      isVerified,
    ],
  )

  const displayAvatar = useMemo(() => {
    if (avatar) {
      return avatar
    }

    if (withAvatar) {
      return <AccountAvatar />
    }

    return null
  }, [avatar, withAvatar])

  const resolvedHref = useMemo(() => {
    if (href) {
      return href
    }
    if (getProfileUrl) {
      return getProfileUrl(accountKey)
    }
    return `/${account.displayName || account.address}`
  }, [href, getProfileUrl, accountKey, account.displayName, account.address])

  const content = (
    <AccountLockupContext.Provider value={value}>
      <FlexCenter
        className={classNames(
          "gap-2 text-sm",
          isCurrentUser && displayYou && linkVariants(),
          className,
        )}
        {...rest}
      >
        {displayAvatar}
        {children ?? (
          <TextOverflowTooltip disabled={disableTextOverflowTooltip}>
            <AccountText />
          </TextOverflowTooltip>
        )}
        {badge ? badge : isVerified && <AccountBadge />}
      </FlexCenter>
    </AccountLockupContext.Provider>
  )

  if (isLink && resolvedHref) {
    return (
      <Link
        className="max-w-full"
        disabled={disabled}
        href={
          typeof resolvedHref === "object" && collectionSlug
            ? {
                ...resolvedHref,
                query: {
                  ...(typeof resolvedHref.query === "object" &&
                  resolvedHref.query !== null
                    ? resolvedHref.query
                    : {}),
                  collectionSlugs: collectionSlug,
                },
              }
            : resolvedHref
        }
        onClick={e => {
          e.stopPropagation()
        }}
        onNavigationCompleted={onNavigationCompleted}
        prefetch="onHover"
        variant="unstyled"
        {...overrides?.Link}
      >
        {content}
      </Link>
    )
  }

  return content
}

const AccountAvatar = ({
  className,
  size = 24,
  ...rest
}: Omit<AvatarImageProps, "src">) => {
  const { address, imageUrl } = useContext(AccountLockupContext)

  return (
    <Avatar
      className={className}
      rounded
      size={size}
      {...(imageUrl ? { src: imageUrl } : { seed: address })}
      {...rest}
      renderImage={AvatarImage}
    />
  )
}

const AccountText = () => {
  const { displayName, address, addressDisplay } =
    useContext(AccountLockupContext)

  return displayName
    ? displayName
    : formatAddress(address, { display: addressDisplay })
}

const AccountBadge = ({
  className,
  size = 14,
  ...rest
}: IconProps & {
  innerFill?: IconProps["fill"]
}) => {
  return (
    <Verified
      className={classNames(className)}
      fill="text-primary"
      innerFill="transparent"
      size={size}
      {...rest}
    />
  )
}

export {
  AccountLockup,
  AccountAvatar as AccountLockupAvatar,
  AccountText as AccountLockupText,
  AccountBadge as AccountLockupBadge,
}
